import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import PdfFeedbackViewer from "./PdfFeedbackViewer";
import "./EventDetails.css";

/**
 * PdfFeedbackPage - Full-page viewer for PDF feedback submissions
 * Displays customer feedback in full-size layout (not a modal)
 */
function PdfFeedbackPage() {
  const { eventId, submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // Fetch submission and PDF info
  useEffect(() => {
    (async () => {
      try {
        console.log("Fetching feedback for event:", eventId, "submission:", submissionId);
        
        // Get all submissions and find the one we need
        const allRes = await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf/submissions`);
        console.log("Submissions response status:", allRes.status);
        
        if (!allRes.ok) {
          throw new Error(`Failed to load submissions: ${allRes.status}`);
        }
        
        const data = await allRes.json();
        console.log("Submissions data:", data);
        
        const sub = data.submissions?.find(s => s.id === parseInt(submissionId));
        if (!sub) {
          throw new Error(`Submission ${submissionId} not found in event ${eventId}`);
        }
        
        console.log("Found submission:", sub);
        setSubmission(sub);

        // Get PDF URL from event
        const pdfRes = await fetch(`${API_BASE_URL}/api/events/${eventId}/pdf`);
        console.log("PDF response status:", pdfRes.status);
        
        if (!pdfRes.ok) {
          throw new Error(`Failed to load PDF: ${pdfRes.status}`);
        }
        
        const pdfData = await pdfRes.json();
        console.log("PDF data:", pdfData);
        
        const url = pdfData.pdf?.pdf_url;
        if (!url) {
          throw new Error("PDF URL not available");
        }
        
        setPdfUrl(url);
        setLoading(false);
      } catch (err) {
        console.error("Error loading feedback:", err);
        setError(err.message || "Failed to load feedback");
        setLoading(false);
      }
    })();
  }, [eventId, submissionId]);

  if (loading) {
    return (
      <div className="ed-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="ed-main-content" style={{ marginLeft: sidebarOpen ? "250px" : "0" }}>
          <div className="ed-header">
            <button className="ed-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ marginRight: "12px" }}>☰</button>
            <h1 className="ed-page-title">Loading feedback...</h1>
          </div>
          <div className="ed-main" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", color: "#667eea" }}>
              <p>Loading customer feedback...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !submission || !pdfUrl) {
    return (
      <div className="ed-container">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="ed-main-content" style={{ marginLeft: sidebarOpen ? "250px" : "0" }}>
          <div className="ed-header">
            <button className="ed-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ marginRight: "12px" }}>☰</button>
            <button className="ed-back-btn" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="ed-page-title">Error loading feedback</h1>
          </div>
          <div className="ed-main" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ textAlign: "center", color: "#f87171" }}>
              <p>{error || "Failed to load feedback"}</p>
              <button className="ed-back-btn" onClick={() => navigate(-1)} style={{ marginTop: "16px" }}>
                ← Back to Event
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ed-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="ed-main-content" style={{ marginLeft: sidebarOpen ? "250px" : "0" }}>
        <div className="ed-header">
          <button className="ed-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ marginRight: "12px" }}>☰</button>
          <button className="ed-back-btn" onClick={() => navigate(-1)}>← Back</button>
          <h1 className="ed-page-title">
            📋 Feedback from {submission.client_name}
          </h1>
        </div>
        <div className="ed-main" style={{ flex: 1, overflow: "hidden", padding: 0 }}>
          <div style={{ height: "100%", width: "100%" }}>
            <PdfFeedbackViewer
              pdfUrl={pdfUrl}
              feedbackData={submission.pages || []}
              initialPage={1}
              onClose={() => navigate(-1)}
              isFullPage={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfFeedbackPage;
